"use client";

import React, {useState} from 'react';
import { Row, Col, Card } from 'antd';
import { useDispatch } from 'react-redux';
import { addProject, clearProject } from "../../../redux/features/userProject/userProjectSlice";
import { UserProjectProps } from '@/types/types';
import { v4 as uuidv4 } from 'uuid';

import SolarStandard from '../../../public/register/solarStandard.svg';
import SolarPowered from '../../../public/register/solarPowered.svg';
import HydroStandard from '../../../public/register/hydroStandard.svg';
import HydroPowered from '../../../public/register/hydroPowered.svg';
import EthereumIcon from '../../../public/common/ethereum.svg';

const { Meta } = Card;

export function generateId() {
  return uuidv4();
}

const buyerPurchaseIdGen = generateId();

interface ProjectSelectProps {
  next: () => void;
  prev: () => void;
}

function addYearsToNow(years: number): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString();
}

export default function ProjectSelect({ next, prev }: ProjectSelectProps) {

  const dispatch = useDispatch();
  const [selectedProject, setSelectedProject] = useState<UserProjectProps['buyerProjectSelected'] | null>(null);

  const handleProjectSelection = (value: UserProjectProps['buyerProjectSelected']) => {
    setSelectedProject(value);
  }

  const projectProperties: UserProjectProps[] = [
    {
      userRegisterId: "",
      userRegisterCreatedAt: new Date().toISOString(),
      buyerProjectSelected: 'solarStandard',
      buyerPurchaseId: buyerPurchaseIdGen,
      buyerPurchasePrice: 1,
      buyerPurchaseCurrency: 'ETH',
      buyerLocation: "", 
      buyerFirstName: "",
      buyerLastName: "",
      buyerEmailAdress: "",
      buyerKycStatus: 'pending',
      projectInterest: 9.2,  
      projectConfirmedMembers: 1,
      projectSupply: 1000,
      projectSize: 0.3,
      projectSizeUnit: 'MW', 
      projectMaturity: addYearsToNow(7),
      projectContingencyRefund: 60, 
      gregOperationalFee: 10, 
      projectCarbonOffset: 1275,
      projectLocalHouseholds: 950,
      projectCurrencyImpact: 285,
      projectStepKey: 'GregBatch01SolStandard',
      Component: SolarStandard,
      projectStepAlt: 'Batch 01 Solar Standard',
      projectStepLabel: 'Solar Standard'
    },
    {
      userRegisterId: "",
      userRegisterCreatedAt: new Date().toISOString(),
      buyerProjectSelected: 'solarPremium',
      buyerPurchaseId: buyerPurchaseIdGen,
      buyerPurchasePrice: 3,
      buyerPurchaseCurrency: 'ETH',
      buyerLocation: "", 
      buyerFirstName: "",
      buyerLastName: "",
      buyerEmailAdress: "",
      buyerKycStatus: 'pending',
      projectInterest: 12.7,  
      projectConfirmedMembers: 1,
      projectSupply: 200,
      projectSize: 0.3,
      projectSizeUnit: 'MW', 
      projectMaturity: addYearsToNow(10),
      projectContingencyRefund: 80, 
      gregOperationalFee: 5, 
      projectCarbonOffset: 1275,
      projectLocalHouseholds: 950,
      projectCurrencyImpact: 285,
      projectStepKey: 'GregBatch01SolPremium',
      Component: SolarPowered,
      projectStepAlt: 'Batch 01 Solar Premium',
      projectStepLabel: 'Solar Premium'
    },
    {
      userRegisterId: "",
      userRegisterCreatedAt: new Date().toISOString(),
      buyerProjectSelected: 'hydroStandard',
      buyerPurchaseId: buyerPurchaseIdGen,
      buyerPurchasePrice: 1,
      buyerPurchaseCurrency: 'ETH',
      buyerLocation: "", 
      buyerFirstName: "",
      buyerLastName: "",
      buyerEmailAdress: "",
      buyerKycStatus: 'pending',
      projectInterest: 11.4,  
      projectConfirmedMembers: 1,
      projectSupply: 1000,
      projectSize: 0.45,
      projectSizeUnit: 'MW', 
      projectMaturity: addYearsToNow(7),
      projectContingencyRefund: 65, 
      gregOperationalFee: 10, 
      projectCarbonOffset: 2400,
      projectLocalHouseholds: 1800,
      projectCurrencyImpact: 500,
      projectStepKey: 'GregBatch01HydroStandard',
      Component: HydroStandard,
      projectStepAlt: 'Batch 01 Hydro Standard',
      projectStepLabel: 'Hydro Standard'
    },
    {
      userRegisterId: "",
      userRegisterCreatedAt: new Date().toISOString(),
      buyerProjectSelected: 'hydroPremium',
      buyerPurchaseId: buyerPurchaseIdGen,
      buyerPurchasePrice: 3,
      buyerPurchaseCurrency: 'ETH',
      buyerLocation: "", 
      buyerFirstName: "",
      buyerLastName: "",
      buyerEmailAdress: "",
      buyerKycStatus: 'pending',
      projectInterest: 14.6,  
      projectConfirmedMembers: 1,
      projectSupply: 200,
      projectSize: 0.45,
      projectSizeUnit: 'MW', 
      projectMaturity: addYearsToNow(10),
      projectContingencyRefund: 80, 
      gregOperationalFee: 5, 
      projectCarbonOffset: 2400,
      projectLocalHouseholds: 1800,
      projectCurrencyImpact: 500,
      projectStepKey: 'GregBatch01HydroPremium',
      Component: HydroPowered,
      projectStepAlt: 'Batch 01 Hydro Premium',
      projectStepLabel: 'Hydro Premium'
    },
  ];


  const handleNext = () => {
    if (selectedProject) {
      dispatch(clearProject());

      const userChosenProject = projectProperties.find(
        (p) => p.buyerProjectSelected === selectedProject);
      
      if (!userChosenProject) return;

      const currentDate = new Date().toISOString(); 

      const newProjectSelected: UserProjectProps = {
        userRegisterId: "",
        userRegisterCreatedAt: currentDate,
        buyerProjectSelected: userChosenProject.buyerProjectSelected,
        buyerPurchaseId: buyerPurchaseIdGen,
        buyerPurchasePrice: userChosenProject.buyerPurchasePrice,
        buyerPurchaseCurrency: userChosenProject.buyerPurchaseCurrency,
        buyerLocation: '',
        buyerFirstName: '',
        buyerLastName: '',
        buyerEmailAdress: '',
        buyerKycStatus: userChosenProject.buyerKycStatus,
        projectInterest: userChosenProject.projectInterest,
        projectConfirmedMembers: userChosenProject.projectConfirmedMembers,
        projectSupply: userChosenProject.projectSupply,
        projectSize: userChosenProject.projectSize,
        projectSizeUnit: userChosenProject.projectSizeUnit,
        projectMaturity: userChosenProject.projectMaturity,
        projectContingencyRefund: userChosenProject.projectContingencyRefund,
        gregOperationalFee: userChosenProject.gregOperationalFee,
        projectCarbonOffset: userChosenProject.projectCarbonOffset,
        projectLocalHouseholds: userChosenProject.projectLocalHouseholds,
        projectCurrencyImpact: userChosenProject.projectCurrencyImpact,
        projectStepKey: userChosenProject.projectStepKey,
        Component: userChosenProject.Component, 
        projectStepAlt: userChosenProject.projectStepAlt,
        projectStepLabel: userChosenProject.projectStepLabel,
      };

      dispatch(addProject(newProjectSelected));
      next();
    }
  };

  return (
    <div className="register-select-container">
      <Row  className="register-select-row" >
        <Col
          xs={{ span: 24 }}
          md={{ span: 24 }}
          sm={{ span: 24 }}
          lg={{ span: 24 }}
          className="register-col-project-img"
        >
          <div className="project-images">
            {projectProperties.map((project) => (
               <div
                  key={project.projectStepKey}
                  className={`project-image ${
                     selectedProject === project.projectStepKey ? "selected" : ""
                  }`}
                  onClick={() => handleProjectSelection(project.buyerProjectSelected)}
              >
                <Col
                  xs={{ span: 24 }}
                  md={{ span: 24 }}
                  sm={{ span: 24 }}
                  lg={{ span: 24 }}
                  className="project-image-col-card"
                >
                  <Card
                    hoverable
                    className="project-image-card-wrapper"
                    cover={
                      <div>
                        <project.Component width={300} height={250} />
                      </div>
                    }
                  >
                    <Meta
                      title={
                        <div className="project-image-card-meta">
                          <div className="project-card-price-container">
                            <EthereumIcon
                              style={{ marginRight: 5, padding: 1 }}
                            />
                            <div className="project-card-price">
                              {project.buyerPurchasePrice}
                            </div>
                            <div className="project-card-currency">
                              {project.buyerPurchaseCurrency}
                            </div>
                          </div>

                          <div className="project-card-label">
                            {project.projectStepLabel}
                          </div>
                          <div className="project-card-interest">
                            <p className="project-card-fixed-interest">
                              Fixed Interest:{" "}
                            </p>
                            {project.projectInterest}%
                          </div>
                        </div>
                      }
                      description={
                        <div>
                          <div className="project-card-description-wrapper">
                            Carbon Offset:{""}
                            <div className="project-card-description-item">
                              {project.projectCarbonOffset} mtCO₂/year
                            </div>
                          </div>
                          <div className="project-card-description-wrapper">
                            Households:{""}
                            <div className="project-card-description-item">
                              {" "}
                              {project.projectLocalHouseholds} per year
                            </div>
                          </div>
                          <div className="project-card-description-wrapper">
                            Impact:  {" "}
                            <div className="project-card-description-item">
                              $US {project.projectCurrencyImpact}k
                            </div>
                          </div>
                        </div>
                      }
                    />
                  </Card>
                </Col>
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <div style={{ marginTop: "20px", textAlign: "right" }}>
        <button
          disabled={!selectedProject}
          onClick={handleNext}
          className="project-select-next-button"
        >
          Next
        </button>
      </div>
    </div>
  );
}
